import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusPill, fmtDate } from "./langStatus";

export const DOC_FIELD_LABELS = {
  title: "Título del documento", header_text: "Cabecera", footer_text: "Pie del documento",
  default_observations: "Observaciones por defecto", legal_terms: "Condiciones", data_protection: "Protección de datos (RGPD)",
  legal_notice: "Aviso legal", signature: "Firma", payment_instructions: "Instrucciones de pago", lines: "Líneas de la etiqueta",
};
const EMAIL_FIELD_LABELS = { subject: "Asunto", body_html: "HTML completo del email", body: "Texto plano / WhatsApp" };

const toText = (v) => (Array.isArray(v) ? v.join("\n") : v || "");

export function TemplateTranslationModal({ group, tplKey, tplLabel, lang, langName, detail, onClose, onSaved, fieldLabels }) {
  const isEmail = group === "emails";
  const isPublic = group === "public";
  const labels = fieldLabels || (isEmail ? EMAIL_FIELD_LABELS : DOC_FIELD_LABELS);
  const fieldKeys = Object.keys(detail.master_fields || {});
  const init = useMemo(() => Object.fromEntries(fieldKeys.map((f) => [f, toText(detail.fields[f]) || toText(detail.master_fields[f])])), [detail]); // eslint-disable-line
  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);
  const dirty = fieldKeys.some((f) => form[f] !== init[f]);
  const hasHtml = isEmail && fieldKeys.includes("body_html");

  const save = async () => {
    setBusy(true);
    try {
      const fields = Object.fromEntries(fieldKeys.map((f) => [f, f === "lines" ? form[f].split("\n") : form[f]]));
      await api.put(`/templates/i18n/${group}/${tplKey}/${lang}`, { fields });
      toast.success(`Traducción ${langName} guardada como revisada manualmente`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const area = (f, rows, cls = "") => (
    <div key={f} className={`flex flex-col ${cls}`}>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
        {labels[f] || f}
        {isPublic && <span className="normal-case tracking-normal text-[#A8A8A0]"> · una línea por texto ({toText(detail.master_fields[f]).split("\n").length} líneas)</span>}
      </label>
      <textarea
        value={form[f]} rows={rows} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
        data-testid={`tpl-i18n-modal-${f}`}
        className={`w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-xs rounded-none font-mono ${cls ? "flex-1 resize-none" : ""}`}
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[92vh] p-0 gap-0 flex flex-col rounded-none border-[#111110]" data-testid="tpl-i18n-modal">
        <div className="px-6 py-4 border-b border-[#E5E5E0] bg-[#F7F7F4] flex items-start justify-between gap-4 flex-wrap">
          <div>
            <DialogTitle className="font-display font-bold text-lg">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D] block mb-1">// Idioma: {langName} ({lang})</span>
              Plantilla: {tplLabel}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#525252] mt-1">
              Edita la traducción. Las variables {"{{...}}"} deben coincidir exactamente con la versión en español. Al guardar quedará marcada como <b>revisada manualmente</b>.
            </DialogDescription>
          </div>
          <div className="flex items-center gap-3 text-xs mr-8">
            <StatusPill st={{ status: detail.status, reviewed: detail.reviewed }} testid="tpl-i18n-modal-status" />
            <span className="text-[#525252]">Traducida: <b data-testid="tpl-i18n-modal-date">{fmtDate(detail.generated_at || detail.updated_at)}</b></span>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-6">
          {hasHtml ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full min-h-[60vh]">
              <div className="flex flex-col gap-3 min-h-0">
                {fieldKeys.includes("subject") && (
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1 block">Asunto</label>
                    <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="tpl-i18n-modal-subject"
                      className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono" />
                  </div>
                )}
                {area("body_html", 24, "flex-1 min-h-0")}
                {fieldKeys.includes("body") && area("body", 5)}
              </div>
              <div className="flex flex-col min-h-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">Vista previa (variables sin sustituir)</div>
                <iframe title="Vista previa traducción" srcDoc={form.body_html} className="flex-1 min-h-[50vh] w-full border border-[#E5E5E0] bg-white" data-testid="tpl-i18n-modal-preview" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {fieldKeys.map((f) => area(f, isPublic ? toText(detail.master_fields[f]).split("\n").length + 1 : f === "title" || f === "legal_notice" || f === "header_text" ? 2 : 8))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E5E5E0] bg-[#F7F7F4] flex items-center justify-between gap-3">
          <div className="text-[11px] text-[#525252]">
            {dirty ? "Hay cambios sin guardar · se marcará como revisada manualmente" : "Sin cambios"}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="tpl-i18n-modal-cancel"
              className="border border-[#111110] hover:bg-[#111110] hover:text-white px-4 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
              <X size={12} /> Cancelar
            </button>
            <button onClick={save} disabled={busy || !dirty} data-testid="tpl-i18n-modal-save"
              className="bg-[#6E8B3D] hover:bg-[#556C2E] disabled:bg-[#A8A8A0] text-white px-4 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
              <Save size={12} /> {busy ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
